import {
	getFragmentsFromSelection,
	getLocalIdsFromSelection,
} from '@atlaskit/editor-common/selection';
import type { EditorCommand, ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type { MalleableUiPlugin } from '../malleableUiPluginType';
import type { RemixModalMetadata, StartRemixOptions } from '../types';
import { normalizeRemixSelectionContext } from './normalize-remix-selection-context';
import { makeSelectRemixAnchor } from './select-remix-anchor';
import { makeStartRemixThinking } from './start-remix-thinking';

export type { StartRemixOptions };

type MatchedAnchor = {
	from: number;
	nodePos: number;
	nodeType: string;
	to: number;
};

/** Finds nodes matching `localIds` in doc order; `from`/`to` are each block's content span. */
const findMatchedAnchors = (
	doc: Parameters<EditorCommand>[0]['tr']['doc'],
	localIds: string[],
): MatchedAnchor[] => {
	const wanted = new Set(localIds);
	const matches: MatchedAnchor[] = [];

	if (wanted.size === 0) {
		return matches;
	}

	doc.descendants((node, pos) => {
		const id = node.attrs?.localId;
		if (typeof id === 'string' && wanted.has(id)) {
			matches.push({
				nodePos: pos,
				nodeType: node.type.name,
				from: pos + 1,
				to: pos + node.nodeSize - 1,
			});
			return false;
		}
		return true;
	});

	return matches;
};

/** Modal-free remix: anchor → capture selection context → start thinking, in one transaction. */
export const makeStartRemix =
	(api: ExtractInjectionAPI<MalleableUiPlugin> | undefined) =>
	({ localIds, remixModalMetadata }: StartRemixOptions): EditorCommand =>
	({ tr }) => {
		const matches = findMatchedAnchors(tr.doc, localIds ?? []);

		if (matches.length === 1) {
			makeSelectRemixAnchor(api)({
				nodePos: matches[0].nodePos,
				nodeType: matches[0].nodeType,
				source: remixModalMetadata.remixSource,
			})({ tr });
		} else if (matches.length > 1) {
			makeSelectRemixAnchor(api)({
				from: Math.min(...matches.map((m) => m.from)),
				to: Math.max(...matches.map((m) => m.to)),
				source: remixModalMetadata.remixSource,
			})({ tr });
		}

		const normalizedContext = normalizeRemixSelectionContext(tr, api);
		const selection = tr.selection;
		const capturedSelectionContext: RemixModalMetadata['capturedSelectionContext'] =
			normalizedContext ?? {
				selectionFragment: getFragmentsFromSelection(selection),
				localIds: getLocalIdsFromSelection(selection),
				selectionMarkdown: null,
				startIndex: selection.$from.parentOffset,
				endIndex: selection.$to.parentOffset,
			};

		makeStartRemixThinking(api)({
			remixModalMetadata: {
				...remixModalMetadata,
				capturedSelectionContext,
				...(normalizedContext ? { hasNormalizedSelection: true } : {}),
			},
		})({ tr });

		return tr;
	};
